/**
 * v3.99ak — Phase 3: "Protected by CannaForge Vault™" public badge.
 *
 * Lightweight component — fetches /vault/strain-status for a strain and
 * renders the certificate pill when preserved. Hides itself entirely
 * when the strain has no active public preservation.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { ShieldCheck } from "lucide-react";

const PLAN_LABEL = {
  starter: "Starter Vault",
  breeder: "Breeder Vault",
  legacy:  "Legacy Vault",
  private: "Private Vault",
};

export default function VaultBadge({ strainId, compact = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!strainId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: resp } = await api.get("/vault/strain-status", { params: { strain_id: strainId } });
        if (!cancelled && resp?.preserved) setData(resp);
      } catch { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, [strainId]);

  if (!data || !data.preserved) return null;

  const planLabel = PLAN_LABEL[data.plan] || "CannaForge Vault";

  if (compact) {
    return (
      <Link to={`/vault/${data.slug || data.preservation_id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border transition-colors hover:border-[var(--gold)]"
            style={{ color: "var(--gold)", borderColor: "var(--gold)",
                     background: "rgba(212,175,55,0.06)" }}
            title={`Protected by CannaForge Vault™ · ${planLabel} · ${data.preservation_id}`}
            data-testid="vault-badge-compact">
        <ShieldCheck className="w-3 h-3" />
        Vault™
      </Link>
    );
  }

  return (
    <Link to={`/vault/${data.slug || data.preservation_id}`}
          className="inline-flex items-center gap-2 px-3 py-1.5 border transition-colors hover:bg-[rgba(212,175,55,0.08)]"
          style={{ color: "var(--gold)", borderColor: "var(--gold)",
                   background: "rgba(212,175,55,0.04)" }}
          data-testid="vault-badge">
      <ShieldCheck className="w-4 h-4" />
      <div className="leading-tight">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em]">
          Protected by CannaForge Vault™
        </div>
        <div className="text-[12px] font-mono">
          {planLabel} · {data.preservation_id}
        </div>
      </div>
    </Link>
  );
}
